import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { usePractice, useSavePractice } from '../lib/queries';
import PracticeMembers from '../components/PracticeMembers';
import Spinner from '../components/Spinner';
import type { Practice } from '../lib/types';

const EMPTY: Partial<Practice> = {
  product: 'bebecare',
  slug: '',
  display_name: '',
  doctor_name: '',
  crm: '',
  tagline: '',
  primary_color: '#1ABC9C',
  logo_url: '',
  plan: 'PEDIATRA',
  max_patients: 50,
  max_doctors: 1,
  active: true,
  features: {},
};

export default function PracticeEdit() {
  const { slug } = useParams<{ slug: string }>();
  const isNew = !slug || slug === 'new';
  const { data, isLoading } = usePractice(isNew ? undefined : slug);
  const save = useSavePractice();
  const navigate = useNavigate();

  const [form, setForm] = useState<Partial<Practice>>(EMPTY);

  useEffect(() => {
    if (data) setForm(data);
  }, [data]);

  if (!isNew && isLoading) return <Spinner />;
  if (!isNew && !isLoading && !data)
    return (
      <div className="alert alert-warning">
        Consultório não encontrado. <Link to="/practices">Voltar</Link>
      </div>
    );

  function patch<K extends keyof Practice>(k: K, v: Practice[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!form.slug || !form.display_name) return;
    try {
      await save.mutateAsync(form as Practice);
      navigate('/practices');
    } catch (err) {
      // erro fica visível via state da mutation
      console.error(err);
    }
  }

  return (
    <div>
      <div className="d-flex align-items-center gap-2 mb-4">
        <Link to="/practices" className="btn btn-sm btn-outline-secondary">
          <i className="bi bi-arrow-left" />
        </Link>
        <h1 className="h3 mb-0">
          {isNew ? 'Novo consultório' : `Editar · ${form.display_name}`}
        </h1>
      </div>

      <form onSubmit={onSubmit} className="row g-4">
        <div className="col-md-8">
          <div className="card shadow-sm">
            <div className="card-body">
              <h2 className="h6 text-muted mb-3">Identificação</h2>

              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label small fw-semibold">
                    Slug (URL: /c/{form.slug || 'slug'})
                  </label>
                  <input
                    className="form-control"
                    value={form.slug ?? ''}
                    onChange={(e) =>
                      patch(
                        'slug',
                        e.target.value
                          .toLowerCase()
                          .replace(/[^a-z0-9-]/g, '-')
                          .replace(/-+/g, '-'),
                      )
                    }
                    required
                    placeholder="dra-fernanda"
                    disabled={!isNew}
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">
                    Produto
                  </label>
                  <select
                    className="form-select"
                    value={form.product ?? 'bebecare'}
                    onChange={(e) => patch('product', e.target.value)}
                  >
                    <option value="bebecare">bebecare</option>
                    <option value="escola">escola</option>
                  </select>
                </div>

                <div className="col-md-12">
                  <label className="form-label small fw-semibold">
                    Nome de exibição
                  </label>
                  <input
                    className="form-control"
                    value={form.display_name ?? ''}
                    onChange={(e) => patch('display_name', e.target.value)}
                    required
                    placeholder="Dra. Fernanda Soares"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">
                    Pediatra responsável
                  </label>
                  <input
                    className="form-control"
                    value={form.doctor_name ?? ''}
                    onChange={(e) => patch('doctor_name', e.target.value)}
                    placeholder="Dra. Fernanda Soares"
                  />
                </div>

                <div className="col-md-6">
                  <label className="form-label small fw-semibold">CRM</label>
                  <input
                    className="form-control"
                    value={form.crm ?? ''}
                    onChange={(e) => patch('crm', e.target.value)}
                    placeholder="CRM/SP 123456"
                  />
                </div>

                <div className="col-md-12">
                  <label className="form-label small fw-semibold">
                    Tagline (subtítulo)
                  </label>
                  <input
                    className="form-control"
                    value={form.tagline ?? ''}
                    onChange={(e) => patch('tagline', e.target.value)}
                    placeholder="Pediatra · 12 anos de experiência"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="card shadow-sm mt-3">
            <div className="card-body">
              <h2 className="h6 text-muted mb-3">Plano e limites</h2>
              <div className="row g-3">
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Plano</label>
                  <select
                    className="form-select"
                    value={form.plan ?? 'PEDIATRA'}
                    onChange={(e) => patch('plan', e.target.value)}
                  >
                    <option value="PEDIATRA">PEDIATRA</option>
                    <option value="CLINICA">CLINICA</option>
                  </select>
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">
                    Famílias
                  </label>
                  <input
                    type="number"
                    min={1}
                    className="form-control"
                    value={form.max_patients ?? 50}
                    onChange={(e) =>
                      patch('max_patients', Number(e.target.value))
                    }
                  />
                </div>
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">
                    Médicos
                  </label>
                  <input
                    type="number"
                    min={1}
                    className="form-control"
                    value={form.max_doctors ?? 1}
                    onChange={(e) =>
                      patch('max_doctors', Number(e.target.value))
                    }
                  />
                </div>
              </div>
            </div>
          </div>

          {!isNew && data?.id && (
            <PracticeMembers practiceId={data.id} practiceSlug={data.slug} />
          )}
        </div>

        <div className="col-md-4">
          <div className="card shadow-sm">
            <div className="card-body">
              <h2 className="h6 text-muted mb-3">Branding</h2>

              <label className="form-label small fw-semibold">
                Cor primária
              </label>
              <div className="input-group mb-3">
                <input
                  type="color"
                  className="form-control form-control-color"
                  value={form.primary_color ?? '#1ABC9C'}
                  onChange={(e) => patch('primary_color', e.target.value)}
                />
                <input
                  type="text"
                  className="form-control"
                  value={form.primary_color ?? ''}
                  onChange={(e) => patch('primary_color', e.target.value)}
                />
              </div>

              <label className="form-label small fw-semibold">
                URL da logo
              </label>
              <input
                className="form-control"
                value={form.logo_url ?? ''}
                onChange={(e) => patch('logo_url', e.target.value)}
                placeholder="https://…"
              />
              {form.logo_url && (
                <div className="text-center mt-3 p-3 bg-light rounded">
                  <img
                    src={form.logo_url}
                    alt="logo preview"
                    style={{ maxHeight: 80, maxWidth: '100%' }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="card shadow-sm mt-3">
            <div className="card-body">
              <div className="form-check form-switch">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="active"
                  checked={form.active ?? true}
                  onChange={(e) => patch('active', e.target.checked)}
                />
                <label htmlFor="active" className="form-check-label">
                  Ativo (visível em /c/{form.slug || 'slug'})
                </label>
              </div>
            </div>
          </div>

          <div className="d-grid gap-2 mt-3">
            <button
              type="submit"
              className="btn btn-bb"
              disabled={save.isPending}
            >
              {save.isPending ? 'Salvando…' : 'Salvar'}
            </button>
            <Link to="/practices" className="btn btn-outline-secondary">
              Cancelar
            </Link>
          </div>

          {save.error && (
            <div className="alert alert-danger mt-3 small">
              {String((save.error as Error).message)}
            </div>
          )}
        </div>
      </form>
    </div>
  );
}
